import { Room } from './room.js';
import { ProceduralRoom } from './proceduralRoom.js';
import { GRAYBOX_VARIANT_ORDER, GRAYBOX_VARIANTS } from './rooms/templates.js';

const BOSS_SEED_SALT = 0x9e3779b9;
const BOSS_ARENA_SIZE = 84;

function mixSeed(v) {
  let x = v >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x7feb352d);
  x ^= x >>> 15;
  x = Math.imul(x, 0x846ca68b);
  x ^= x >>> 16;
  return x >>> 0;
}

function bossRoomSeedFromSession(sessionSeed) {
  return mixSeed((Number(sessionSeed) >>> 0) ^ BOSS_SEED_SALT);
}

function templateForVariant(variantId) {
  const id = typeof variantId === 'string' ? variantId : '';
  return GRAYBOX_VARIANTS[id] || GRAYBOX_VARIANTS[GRAYBOX_VARIANT_ORDER[0]];
}

export function variantForRoom(roomNum, variantOrder, sessionSeed = 0) {
  // Boss rooms (5 and 10) have dedicated arenas and skip graybox variants.
  if (roomNum === 5 || roomNum === 10) return null;
  // Convert absolute room number to non-boss room ordinal:
  // 1,2,3,4,6,7,8,9 -> 1..8
  const nonBossOrdinal = roomNum < 5 ? roomNum : roomNum - 1;
  const order = Array.isArray(variantOrder) && variantOrder.length > 0 ? variantOrder : GRAYBOX_VARIANT_ORDER;
  const rotation = (Number(sessionSeed) >>> 0) % order.length;
  const index = (nonBossOrdinal - 1 + rotation) % order.length;
  return order[index];
}

export function buildCurrentRoom(game, { sandboxTemplate, variants }) {
  const roomNum = game.roomIndex + 1;
  game._spawnRoomClearedHandled = false;
  let variantId = null;
  if (game.runMode === 'sandbox') {
    game._lastRoomSeed = 0;
    game.room = new Room({
      scene: game.scene,
      template: sandboxTemplate,
      propTemplates: game.kitbashProps,
    });
  } else {
    variantId = variantForRoom(roomNum, GRAYBOX_VARIANT_ORDER, game._sessionSeed);
    const isBossRoom = roomNum === 5 || roomNum === 10;
    const roomSeed = isBossRoom
      ? bossRoomSeedFromSession(game._sessionSeed)
      : game._roomSeedFor(roomNum);
    game._lastRoomSeed = roomSeed;
    if (isBossRoom) {
      game.room = new ProceduralRoom({
        scene: game.scene,
        seed: roomSeed,
        wallThicknessScale: game.wallThicknessScale,
        layoutType: 'bossArena',
        arenaSize: BOSS_ARENA_SIZE,
      });
    } else {
      const selectedTemplate = templateForVariant(variantId);
      game.room = new Room({
        scene: game.scene,
        template: selectedTemplate,
        propTemplates: game.kitbashProps,
      });
    }
    const roomDebug = game.room.getDebugInfo?.();
    console.info('[seed-debug] procedural room built', {
      roomNum,
      sessionSeed: game._sessionSeed >>> 0,
      roomSeed,
      doorA: roomDebug?.doorA || null,
      doorB: roomDebug?.doorB || null,
    });
  }
  game.currentRoomVariant = variantId;
  const minimapCanvas = document.getElementById('minimap-canvas');
  game.minimap?.init(minimapCanvas, game.room?.getBounds?.() || game.room?.getAtmosphereBounds?.());
  game.minimap?.setDoorPosition(game.room?.getDoorPosition?.() || game.room?.door || null);
  // Proactively prefetch upcoming bosses so FBX is (likely) ready on entry.
  if (game.runMode === 'run') {
    if (roomNum >= 3 && roomNum <= 5) game._triggerBossPreload(1);
    if (roomNum >= 8 && roomNum <= 10) game._triggerBossPreload(2);
  }

  game.player.x = game.room.playerSpawn.x;
  game.player.z = game.room.playerSpawn.z;
  game.player.group.position.set(game.player.x, 0, game.player.z);
  if (game.roomIndex === 0) game.player.onLevelStart();
  game.cameraRig.setFollow(game.player.x, game.player.z);
  if (game.runMode === 'sandbox') {
    game.room.unlockDoor();
  } else {
    const enemyManager = {
      spawnEnemy: (type, pos) => game._addEnemyByType?.(type, pos.x, pos.z),
    };
    game.spawnRuntime?.initRoom(
      roomNum,
      game.room?.getBounds?.() || game.room?.getAtmosphereBounds?.(),
      game.player?.group?.position || game.room?.playerSpawn,
      (type, pos) => enemyManager.spawnEnemy(type, pos) || ({ alive: true, x: pos.x, z: pos.z }),
      () => (game.room?.openExitDoor?.() || game.room?.unlockDoor?.()),
    );
  }
  game.hud.showStageIntro(roomNum);
  if (variantId) {
    const role = variants?.[variantId]?.role || 'seeded-layout';
    game.toast(`BLOCKOUT ${variantId}: ${String(role).toUpperCase()}`);
  }
  game.hud.update(game.player, game.roomIndex, game.totalRooms);
  game._captureRoomEntrySnapshot?.();
}


